import React from "react";
import { Lock, Truck, Package, X } from "lucide-react";
import { format, parseISO } from "date-fns";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import { ExpandableHeader } from "../../../../../components/ACS-Dashboard/ACS-Blocks/ExpandableHeader";
import { DisplayDetail }    from "../../../../../components/ACS-Dashboard/ACS-Blocks/DisplayDetail";
import Loader               from "../../../../../components/ACS-Dashboard/ACS-Blocks/Loader";
import { addTab as addShipmentTab } from "../../../../../redux/slices/shipmentSlice";
import { formatSlotTime, formattedTime, trimTime } from "./utils";

// ─── ReadOnlyBanner ───────────────────────────────────────────────────────────
export function ReadOnlyBanner() {
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex items-center gap-2 rounded-md border border-gray-300 bg-gray-50 text-gray-700 px-3 py-2 text-sm"
    >
      <Lock className="w-4 h-4 shrink-0 text-gray-500" aria-hidden="true" />
      <span>
        This appointment is in <strong>read-only</strong> mode while in-transit.
        No changes can be made.
      </span>
    </div>
  );
}

// ─── CarrierDropTrailerBanner ─────────────────────────────────────────────────
export function CarrierDropTrailerBanner() {
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 text-amber-800 px-3 py-2 text-sm"
    >
      <Lock className="w-4 h-4 shrink-0 text-amber-600" aria-hidden="true" />
      <span>
        This is a <strong>drop trailer</strong> appointment. Carriers can only
        view drop trailer appointments and cannot add or edit them.
      </span>
    </div>
  );
}

// ─── CancelledBanner ──────────────────────────────────────────────────────────
export function CancelledBanner() {
  return (
    <div role="alert" className="rounded-md border border-red-300 bg-red-50 text-red-800 px-3 py-2 text-sm">
      This appointment has been cancelled. Details are read-only.
    </div>
  );
}

// ─── AppointmentTypeBadge ─────────────────────────────────────────────────────
export function AppointmentTypeBadge({ isDropTrailer, label, pending }) {
  // The authoritative type comes from the shipment detail, which loads async.
  // Until it resolves, show a neutral placeholder rather than guessing (which
  // would flash the wrong type — e.g. "Live Load" — before correcting).
  if (pending) {
    return (
      <span
        aria-busy="true"
        className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-semibold border bg-gray-100 text-gray-500 border-gray-200"
      >
        <span
          className="w-4 h-4 rounded-full border-2 border-gray-300 border-t-gray-500 animate-spin"
          aria-hidden="true"
        />
        Determining type…
      </span>
    );
  }

  const colorClasses = isDropTrailer
    ? "bg-orange-100 text-orange-800 border-orange-200"
    : "bg-emerald-100 text-emerald-800 border-emerald-200";
  return (
    <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-semibold border ${colorClasses}`}>
      {isDropTrailer
        ? <Package className="w-4 h-4" aria-hidden="true" />
        : <Truck   className="w-4 h-4" aria-hidden="true" />
      }
      {label}
    </span>
  );
}

// ─── DockSlotsSection ─────────────────────────────────────────────────────────
export function DockSlotsSection({
  appointmentData, isCancelled, isReadOnlyMode,
  allDockSlots, isAllSlotsLoading, allSlotsError,
  selectedSlot, selectedDocks, onSelectSlot,
  date, selectedWarehouse, dockOptions,
}) {
  const isSlotSelected = (dock, slot) =>
    selectedSlot?.startTime === slot.startTime &&
    String(selectedDocks?.dockId) === String(dock.dockId);

  const slotButtonClasses = (dock, slot) => {
    const selected = isSlotSelected(dock, slot);
    const base = "border rounded-md px-3 py-2 text-sm flex items-center justify-between transition";
    if (isReadOnlyMode) {
      return selected
        ? `${base} bg-[#223688] text-white border-[#223688] cursor-default`
        : `${base} border-gray-300 text-gray-500 cursor-default bg-gray-50`;
    }
    return selected
      ? `${base} bg-[#223688] text-white border-[#223688]`
      : `${base} hover:bg-[#eef1ff] border-[#223688] text-[#223688]`;
  };

  return (
    <ExpandableHeader headerSection="All Dock Slots" headerLabel="" headerType="secondary" disableToggle initiallyExpanded>
      <div className="border-t border-gray-200 p-4 bg-white shadow-sm max-h-[40rem] overflow-y-auto">
        {isCancelled ? (
          <p className="text-sm text-red-700">Slot selection is unavailable — this appointment has been cancelled.</p>
        ) : (
          <>
            <div className="mb-3 grid grid-cols-1 sm:grid-cols-5 gap-3">
              <DisplayDetail
                label="Scheduled Date"
                value={appointmentData?.startTime ? formattedTime(appointmentData.startTime, "EEEE, MMMM d, yyyy") : "N/A"}
              />
              <DisplayDetail
                label="Scheduled Start Time"
                value={appointmentData?.startTime ? formatSlotTime(trimTime(appointmentData.startTime)) : "N/A"}
              />
            </div>

            {(!date || !selectedWarehouse?.locationRef || !dockOptions.length) && (
              <p className="text-sm text-gray-600">Select a date to view dock slots.</p>
            )}
            {isAllSlotsLoading && <Loader message="Loading slots..." compact />}
            {allSlotsError && <p className="text-sm text-red-600">{allSlotsError}</p>}

            {!isAllSlotsLoading && !allSlotsError && allDockSlots.length > 0 && (
              <div className="space-y-3">
                {isReadOnlyMode && (
                  <p className="text-xs text-gray-500 italic">
                    Slots are shown for reference only and cannot be changed in read-only mode.
                  </p>
                )}
                {allDockSlots.map(({ dock, slots: dockSlots }) => (
                  <div key={dock.dockId} className="p-fluid-1">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-semibold text-gray-800">
                        {dock.dockName || `Dock ${dock.dockId}`}
                      </span>
                      <span className="text-xs text-gray-500">{dockSlots?.length || 0} slots</span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2">
                      {Array.isArray(dockSlots) && dockSlots.length > 0 ? (
                        dockSlots.map((slot) => (
                          <button
                            key={`${dock.dockId}-${slot.startTime}`}
                            onClick={() => onSelectSlot(dock, slot, dockSlots)}
                            disabled={isReadOnlyMode}
                            aria-disabled={isReadOnlyMode}
                            aria-pressed={isSlotSelected(dock, slot)}
                            className={slotButtonClasses(dock, slot)}
                          >
                            <span className="font-medium">{formatSlotTime(slot.startTime)}</span>
                            {!isReadOnlyMode && <span className="text-[11px] text-gray-500 ml-3">Select</span>}
                          </button>
                        ))
                      ) : (
                        <p className="text-xs text-red-600 col-span-full">No available slots for this dock.</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {!isAllSlotsLoading && !allSlotsError && allDockSlots.length === 0 &&
              date && selectedWarehouse?.locationRef && dockOptions.length > 0 && (
                <p className="text-sm text-gray-600">No docks returned slots.</p>
              )}
          </>
        )}
      </div>
    </ExpandableHeader>
  );
}

// ─── ShipmentSection ──────────────────────────────────────────────────────────
export function ShipmentSection({ appointmentData, shipmentData, fieldConfig, isReadOnlyMode, isEditMode }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleViewShipment = () => {
    const shipmentNumber = appointmentData?.shipmentNumber;
    if (!shipmentNumber) return;
    dispatch(addShipmentTab({
      id:                `view-${shipmentNumber}-${Date.now()}`,
      name:              shipmentNumber,
      contentType:       "shipmentView",
      shipmentNumber,
      shipmentOfficeCode:
        shipmentData?.officeCode           ||
        appointmentData?.officeCodeShipper ||
        appointmentData?.shipmentOfficeCode||
        appointmentData?.officeCode        ||
        Cookies.get("officeCode"),
      isDynamic: true,
      createdAt: Date.now(),
    }));
    navigate("/dashboard/shipments");
  };

  const headerLabel = appointmentData.shipmentNumber ? (
    <button
      type="button"
      onClick={handleViewShipment}
      className="text-white underline decoration-white decoration-2 underline-offset-2"
    >
      {appointmentData.shipmentNumber}
    </button>
  ) : "";

  return (
    <ExpandableHeader headerSection="Shipment" headerLabel={headerLabel} icon={Truck} headerType="secondary">
      <div className="px-4 py-3 mb-2">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
          {Object.entries(fieldConfig).map(([key, config]) => (
            <DisplayDetail
              key={key}
              label={config.label}
              value={config.value}
              inputType={config.inputType}
              isEditable={isReadOnlyMode ? false : isEditMode}
              isReadOnly={isReadOnlyMode ? true : (config.isReadOnly ?? true)}
            />
          ))}
        </div>
      </div>
    </ExpandableHeader>
  );
}

// ─── CarrierSection ───────────────────────────────────────────────────────────
export function CarrierSection({ appointmentData, fieldConfig, isReadOnlyMode, isEditMode, onCarrierChange }) {
  const canEdit = isEditMode && !isReadOnlyMode;
  return (
    <ExpandableHeader
      headerSection="Carrier"
      headerLabel={appointmentData.carrierName ? `\u00A0${appointmentData.carrierName}` : ""}
      icon={Package}
      headerType="secondary"
    >
      <div className="px-4 py-3 mb-2">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-3 gap-2">
          {Object.entries(fieldConfig).map(([key, config]) => (
            <DisplayDetail
              key={key}
              label={config.label}
              value={config.value}
              inputType={config.inputType}
              isEditable={canEdit}
              isReadOnly={isReadOnlyMode ? true : (config.isReadOnly ?? false)}
              onChange={
                canEdit && !config.isReadOnly
                  ? (e) => {
                      const raw = e?.target?.value ?? e;
                      onCarrierChange(key, config.inputType === "number" ? Number(raw) : raw);
                    }
                  : undefined
              }
            />
          ))}
        </div>
      </div>
    </ExpandableHeader>
  );
}

// ─── AllSlotsModal ────────────────────────────────────────────────────────────
export function AllSlotsModal({ isOpen, onClose, date, allDockSlots, isLoading, error, isReadOnlyMode, onSelectSlot }) {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-5xl max-h-[80vh] overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Available Slots by Dock</h3>
            <p className="text-sm text-gray-500">
              {date ? `For ${format(parseISO(date), "EEEE, MMM d, yyyy")}` : "Select a date to view slots"}
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close modal" className="text-gray-500 hover:text-gray-700">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto max-h-[70vh]">
          {isLoading && <div className="flex items-center justify-center py-10"><Loader message="Loading slots..." /></div>}
          {error    && <div className="bg-red-50 text-red-700 border border-red-200 rounded-md px-4 py-3 text-sm">{error}</div>}
          {!isLoading && !allDockSlots.length && !error && (
            <p className="text-sm text-gray-600">No docks returned slots. Try another date or warehouse.</p>
          )}

          {allDockSlots.map(({ dock, slots: dockSlots }) => (
            <div key={dock.dockId} className="border border-gray-200 rounded-md p-4 shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="px-2 py-1 rounded bg-gray-100 text-xs font-semibold text-gray-700">
                    Dock #{dock.dockId}
                  </span>
                  <span className="text-sm text-gray-800 font-medium">{dock.dockName || "Unnamed Dock"}</span>
                </div>
                <span className="text-xs text-gray-500">{dockSlots?.length || 0} slots</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {Array.isArray(dockSlots) && dockSlots.length > 0 ? (
                  dockSlots.map((slot) => (
                    <button
                      key={`${dock.dockId}-${slot.startTime}`}
                      onClick={() => onSelectSlot(dock, slot)}
                      disabled={isReadOnlyMode}
                      className={`border rounded-md px-3 py-2 text-sm flex items-center justify-between transition ${
                        isReadOnlyMode
                          ? "border-gray-300 text-gray-500 cursor-default bg-gray-50"
                          : "hover:bg-[#eef1ff] border-[#223688] text-[#223688]"
                      }`}
                    >
                      <span className="font-medium">{formatSlotTime(slot.startTime)}</span>
                      {!isReadOnlyMode && <span className="text-[11px] text-gray-500 ml-3">Select</span>}
                    </button>
                  ))
                ) : (
                  <p className="text-sm text-gray-500 col-span-full">No available slots for this dock.</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}